/**
 * Переключатель тира прямо в строке игрока.
 *
 * Кнопка на каждый тир, а не выпадающий список: тиры правят пачкой, и лишнее
 * нажатие на каждой строке складывается в заметную работу.
 *
 * Показанное значение опережает сервер намеренно. Тир меняется в базе сразу,
 * а роль в Discord — фоном, поэтому ответ приходит быстро; но пока он идёт,
 * кнопка обязана выглядеть нажатой, иначе жмут второй раз. Отказ возвращает
 * прежнее значение и говорит об этом вслух.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { tierLabel, type Tier } from '@ak/shared';

import { apiPost } from '../lib/api.ts';
import { useToast } from './toast.tsx';
import { tierColor } from './ui.tsx';

const LEVELS: readonly Tier[] = [0, 1, 2, 3, 4];

export function TierPicker({ userId, tier }: { userId: string; tier: Tier }): ReactNode {
  const toast = useToast();
  /*
   * Что мы отправили и чего ещё не подтвердил сервер. Пока это значение
   * живо, оно и рисуется: сравнение с присланным тиром — единственный
   * признак того, что сервер догнал.
   */
  const [pending, setPending] = useState<Tier | null>(null);
  const shown = pending ?? tier;

  useEffect(() => {
    if (pending !== null && tier === pending) {
      setPending(null);
    }
  }, [tier, pending]);

  const change = (next: Tier): void => {
    if (next === shown) {
      return;
    }

    setPending(next);
    apiPost(`/api/players/${userId}/tier`, { tier: next }).catch((cause: unknown) => {
      setPending(null);
      toast.error(cause instanceof Error ? cause.message : 'Не удалось сменить тир');
    });
  };

  return (
    <div className="inline-flex overflow-hidden rounded-md" style={{ border: '1px solid var(--border-strong)' }}>
      {LEVELS.map((level) => {
        const active = level === shown;
        // Подпись и подсказка считаются один раз: разойтись они не должны,
        // а на кнопке помещается только уплотнённый вариант.
        const name = level === 0 ? 'Снять тир' : tierLabel(level);

        return (
          <button
            key={level}
            type="button"
            aria-label={name}
            aria-pressed={active}
            title={name}
            onClick={(event) => {
              // В статистике вся строка — ссылка на карточку игрока, и без
              // этого нажатие на тир уводило со страницы вместо смены роли.
              event.preventDefault();
              event.stopPropagation();
              change(level);
            }}
            className="min-w-8 px-2.5 py-1.5 text-sm leading-none font-semibold transition-colors"
            style={{
              // Цвет тира взят фоном только у выбранной кнопки: ряд
              // цветных квадратов в строке спорил бы с плашкой ника.
              background: active && level !== 0 ? tierColor(level) : 'transparent',
              color: active ? (level === 0 ? 'var(--text-primary)' : 'var(--accent-text)') : 'var(--text-muted)',
              borderLeft: level === 0 ? undefined : '1px solid var(--border-strong)',
            }}
          >
            {level === 0 ? '—' : tierLabel(level, 'short')}
          </button>
        );
      })}
    </div>
  );
}
